import fixtureJson from '../../demo/fixtures/scale-approval.json'
import { formatInteger, formatUsd } from '../../demo/format'
import { useCreativeLibrary } from '../../demo/selectors'
import { useChain } from '../../demo/selectors/chain'
import { useDemoStore } from '../../demo/store'
import { Button, Card, Icon, Term } from '../../ui'
import { fillTemplate, useW5Live } from '../analysis/live'
import layoutStyles from './AutomationPage.module.css'
import styles from './PendingApprovalCard.module.css'
import type { ProposalDialogState, ProposalMode } from './ProposalDecisionDialog'

type ApprovalAction = 'approve' | 'custom' | 'decline'

interface ScaleApprovalFixture {
  meta: { campaignId: string; step: string }
  proposal: {
    type: string
    from: { dailyMin: number; dailyMax: number }
    to: { dailyMin: number; dailyMax: number }
  }
  actions: ApprovalAction[]
  copy: {
    cardTitle: string
    stepChip: string
    budgetNow: string
    budgetAfter: string
    budgetUnit: string
    budgetNote: string
    actions: Record<ApprovalAction, string>
    decisionCount: string
    decided: {
      approved: string
      declined: string
      approvedSummary: string
      declinedSummary: string
      footer: string
      stampApproved: string
      stampDeclined: string
    }
  }
}

const fixture = fixtureJson as ScaleApprovalFixture
const modeByAction: Record<ApprovalAction, ProposalMode> = { approve: 'approve', custom: 'modify', decline: 'reject' }
const variantByAction: Record<ApprovalAction, 'primary' | 'ghost'> = { approve: 'primary', custom: 'ghost', decline: 'ghost' }
const fallbackProductThumb = 'assets/product-mug.jpg'

interface PendingApprovalCardProps {
  onDecide: (request: ProposalDialogState) => void
}

export function PendingApprovalCard({ onDecide }: PendingApprovalCardProps) {
  const { automation: { pending, proposal }, taskRows } = useW5Live()
  const chain = useChain()
  const campaign = useDemoStore((state) => state.campaigns.find((item) => item.id === fixture.meta.campaignId))
  const creative = useCreativeLibrary().find((item) => item.productId === campaign?.productId)
  const task = taskRows.find((item) => item.campaignId === fixture.meta.campaignId)
  const decisionSteps = chain.steps.filter((step) => step.requiresDecision)
  const decisionIndex = decisionSteps.findIndex((step) => step.id === fixture.meta.step) + 1
  const decisionCount = fillTemplate(fixture.copy.decisionCount, { total: decisionSteps.length, index: decisionIndex })
  const from = pending.from
  const proposed = pending.to
  const effective = proposal.state === 'approved' ? (proposal.dailyMax ?? proposed) : proposal.state === 'rejected' ? from : proposed
  const dailyMin = campaign?.budget.dailyMin ?? fixture.proposal.from.dailyMin
  const fromText = formatUsd(from, 0)
  const effectiveText = formatUsd(effective, 0)
  const decidedAt = proposal.decidedAt?.split(' ').at(-1) ?? '—'
  const decide = (action: ApprovalAction) => onDecide({ mode: modeByAction[action], from, to: proposed, rule: pending.rule, obj: pending.obj })
  const title = proposal.state === 'pending'
    ? fixture.copy.cardTitle
    : proposal.state === 'approved' ? fixture.copy.decided.approved : fixture.copy.decided.declined
  const summary = proposal.state === 'approved'
    ? fillTemplate(fixture.copy.decided.approvedSummary, { from: fromText, to: effectiveText, at: decidedAt })
    : fillTemplate(fixture.copy.decided.declinedSummary, { reason: proposal.rejectReason ?? '—', from: fromText })

  if (fixture.proposal.type !== 'budget_cap' || !campaign) return null

  return (
    <Card className={`${layoutStyles.section} ${styles.card}`} data-scope="scale-approval" lang="zh-CN">
      <div className={styles.header}>
        <span className={styles.aiAvatar}><Icon name="sparkle" size={16} strokeWidth={1.7} /></span>
        <h3>{title}</h3>
        <span className={styles.stepChip}>{fixture.copy.stepChip}</span>
      </div>
      <div className={styles.body}>
        <div className={styles.campaign} data-role="campaign-ref">
          <span className={styles.thumbContext} data-context="product-thumb">
            <img className={styles.thumb} src={creative?.cover ?? fallbackProductThumb} alt="" />
          </span>
          <div className={styles.campaignText}>
            <b>{campaign.adName}</b>
            <span> · {task?.status ?? '—'} · {formatInteger(campaign.result.current)} / {formatInteger(campaign.result.target)} 单 · <Term k="cpa" /> {campaign.metrics.cpa === null ? '—' : formatUsd(campaign.metrics.cpa, 2)}</span>
          </div>
        </div>
        {proposal.state === 'pending' ? null : <div className={styles.decisionCopy}><strong>{summary}</strong><span>{fixture.copy.decided.footer}</span></div>}
        <div className={styles.budgetDiff} data-role="budget-diff">
          <div>
            <div className={styles.budgetLabel}>{fixture.copy.budgetNow}</div>
            <div className={styles.budgetValue}>{formatUsd(dailyMin, 0)}–{fromText}{fixture.copy.budgetUnit}</div>
          </div>
          <Icon className={styles.arrow} name="chevron-right" size={20} strokeWidth={1.7} />
          <div>
            <div className={styles.budgetLabel}>{fixture.copy.budgetAfter}</div>
            <div className={`${styles.budgetValue} ${proposal.state === 'rejected' ? '' : styles.approvedBudget}`}>{formatUsd(dailyMin, 0)}–{effectiveText}{fixture.copy.budgetUnit}</div>
          </div>
          <div className={styles.budgetNote}>{fillTemplate(fixture.copy.budgetNote, { from: fromText })}</div>
        </div>
        <div className={styles.actionRow}>
          {proposal.state === 'pending' ? fixture.actions.map((action) => (
            <Button key={action} variant={variantByAction[action]} className={styles.actionButton} data-role={action} onClick={() => decide(action)}>{fixture.copy.actions[action]}</Button>
          )) : (
            <span className={styles.decisionStamp} data-role="decision-stamp"><Icon name="check-circle" size={20} /><span>{proposal.state === 'approved' ? fixture.copy.decided.stampApproved : fixture.copy.decided.stampDeclined}</span></span>
          )}
          <span className={styles.decisionCount}>{decisionCount}</span>
        </div>
      </div>
    </Card>
  )
}
